import React from 'react';
import Image from 'next/image';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Stack,
  Typography,
  List,
  ListItem,
  useTheme,
  styled,
  useMediaQuery,
} from '@mui/material';

import Icon from '@/components/icons/Icon';
import Modal from '@/widgets/Modal';
import { stopPropagation } from '@/utils/common';

import type { StatItem } from './helpers';

const ModalWrapper = styled(Box)`
  min-height: 70vh;
  max-height: calc(95vh - 120px);
  width: 90vw;
  max-width: 748px;

  @media screen and (min-width: 640px) {
    width: 80vw;
  }
`;

const ConsultantReviewModal = ({
  consultant,
  closeModal,
}: {
  consultant: StatItem;
  closeModal: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const isModalOpen = !!consultant;

  const { user, review, results, solution, shortReview } = consultant || {};

  const { name, avatar, position, companyLogo } = user || {};

  const shortReviewComponent = (
    <Stack
      sx={{
        padding: theme.spacing(4),
        width: 'auto',
        height: 'fit-content',
        borderRadius: theme.spacing(3),
        backgroundColor: theme.palette.common.white,
        position: 'relative',
        '&::before': {
          content: '""',
          top: { xs: '-18px', lg: '24px' },
          left: { xs: '16px', lg: '-16.5px' },
          position: 'absolute',
          width: '0',
          height: '0',
          borderBottom: { xs: '20px solid #fff', lg: 'unset' },
          borderRight: { xs: '18px solid transparent', lg: 'unset' },
          borderTop: { xs: 'unset', lg: '23px solid #fff' },
          borderLeft: { xs: 'unset', lg: '16.5px solid transparent' },
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#34353E',
          fontSize: `${theme.spacing(3)} !important`,
          lineHeight: `1.63 !important`,
        }}
      >
        “{shortReview}”
      </Typography>
    </Stack>
  );

  return (
    <Modal
      isOpen={isModalOpen}
      handleClose={closeModal}
      closeOnBackClick
      useOutsideCloseButton
      overlayStyle={{ marginTop: 0, marginLeft: 0 }}
    >
      <ModalWrapper
        onClick={stopPropagation}
        onScroll={stopPropagation}
        sx={{ padding: 'unset' }}
      >
        <Stack
          direction="column"
          sx={{
            // padding: { xs: theme.spacing(6), md: theme.spacing(8) },
            overflowY: 'auto',
            backgroundColor: theme.palette.common.white,

            borderRadius: theme.spacing(4),
          }}
        >
          <Stack
            sx={{
              flexDirection: { xs: 'column', lg: 'row' },
              backgroundColor: '#D5ECEC',
              padding: {
                xs: theme.spacing(3, 5, 5),
                lg: theme.spacing(4, 8, 5),
              },
            }}
            justifyContent="flex-start"
          >
            <Stack>
              <Stack
                sx={{
                  gap: theme.spacing(3),
                  flexDirection: 'row',
                }}
              >
                <Box
                  position="relative"
                  height={{ xs: avatar?.height, lg: avatar?.heightModal }}
                  width={{ xs: avatar?.width, lg: avatar?.widthModal }}
                >
                  <Image
                    src={`/images/home/${avatar?.src}`}
                    layout="fill"
                    objectPosition="center"
                  />
                </Box>

                <Box
                  position="relative"
                  height={companyLogo?.height}
                  width={companyLogo?.width}
                  sx={{
                    marginTop: theme.spacing(1.5),
                  }}
                >
                  <Image
                    src={`/images/home/${companyLogo?.src}`}
                    layout="fill"
                    objectPosition="center"
                  />
                </Box>

                {!isMobile && (
                  <Box
                    sx={{
                      display: { xs: 'none', lg: 'flex' },
                      flex: '1 1 0',
                      my: theme.spacing(1.75),
                      marginLeft: theme.spacing(4),
                    }}
                  >
                    {shortReviewComponent}
                  </Box>
                )}
              </Stack>

              <Stack
                sx={{
                  gap: theme.spacing(1),
                  width: 'max-content',
                  marginTop: { xs: theme.spacing(2), lg: theme.spacing(1) },
                  paddingLeft: theme.spacing(1),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: `${theme.spacing(4.75)} !important`,
                    fontWeight: '700',
                    lineHeight: `1.15789474 !important`,
                    color: '#52545F',
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: `${theme.spacing(3.5)} !important`,
                    fontWeight: '700',
                    lineHeight: `1.4 !important`,
                    color: theme.palette.primary.main,
                  }}
                >
                  {position}
                </Typography>
              </Stack>
            </Stack>

            {isMobile && (
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  marginTop: theme.spacing(7),
                }}
              >
                {shortReviewComponent}
              </Box>
            )}
          </Stack>

          <Stack
            sx={{
              gap: { xs: 0, md: theme.spacing(11) },
              flexDirection: { xs: 'column', md: 'row' },
              padding: { xs: theme.spacing(2, 6), md: theme.spacing(5, 8) },
            }}
          >
            <Stack
              sx={{
                gap: theme.spacing(1.5),
                width: { xs: '100%', md: '36.6%' },
                py: { xs: theme.spacing(2), md: 0 },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#34353E',
                  fontSize: `${theme.spacing(4.5)} !important`,
                  lineHeight: `1.46 !important`,
                }}
              >
                Solution:
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#34353E',
                  fontSize: `${theme.spacing(3)} !important`,
                  lineHeight: `1.63 !important`,
                }}
              >
                {solution}
              </Typography>
            </Stack>

            <Stack
              sx={{
                gap: theme.spacing(1.5),
                width: { xs: '100%', md: '57%' },
                py: { xs: theme.spacing(2), md: 0 },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#34353E',
                  fontSize: `${theme.spacing(4.5)} !important`,
                  lineHeight: `1.46 !important`,
                }}
              >
                Results:
              </Typography>

              <List
                disablePadding
                component="ul"
                sx={{
                  listStyle: 'none',
                  gap: theme.spacing(2),
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {results?.map((result) => (
                  <ListItem
                    key={result}
                    component="li"
                    disablePadding
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      gap: theme.spacing(1.7),
                      fontSize: `${theme.spacing(3)} !important`,
                      lineHeight: `1.63 !important`,
                      color: '#34353E',
                      '&::before': {
                        content: '""',
                        top: '8px',
                        left: '-12px',
                        position: 'absolute',
                        width: '6.55px',
                        height: '6.55px',
                        borderRadius: theme.spacing(1.64),
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {result}
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Stack>

          <Stack
            sx={{
              rowGap: theme.spacing(2.5),
              columnGap: theme.spacing(2),
              flexDirection: { xs: 'column', md: 'row' },
              backgroundColor: theme.palette.common.white,
              padding: {
                xs: theme.spacing(2, 6, 7.5),
                md: theme.spacing(2, 8, 7.5),
              },
            }}
          >
            <Box>
              <Icon
                iconName="quote-mark-open"
                style={{
                  width: '28px',
                  height: '20px',
                  color: theme.palette.primary.main,
                }}
              />
            </Box>

            <Stack direction="column">
              {review?.map((item) => (
                <Typography
                  key={item}
                  variant="body2"
                  sx={{
                    color: '#34353E',
                    fontSize: `${theme.spacing(3.5)} !important`,
                    lineHeight: `1.4 !important`,
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Stack>

            <Box
              sx={{
                marginTop: { xs: 'unset', md: 'auto' },
                marginLeft: { xs: 'auto', md: 'unset' },
              }}
            >
              <Icon
                iconName="quote-mark-close"
                style={{
                  width: '29px',
                  height: '21px',
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </ModalWrapper>
    </Modal>
  );
};

export default observer(ConsultantReviewModal);
